// ---------------------------------------------------------------------------
// SHARED ASSESSMENT HISTORY HELPERS
// ---------------------------------------------------------------------------
// Used by start-assessment.js and Review-assessment.js so both pathways
// resolve the inheritance source and the "Previous Data" panel identically.
//
// get_historical_pathway_data.php returns:
//   {
//     status: true,
//     client_id, client_name, age_group,
//     group_name,
//     groups: ["Demographics", "Background Information & Assessments", ...],
//     data: [ { session_id, appointment_type, assessment_date, status,
//               pathway: [ { node, question, keyword, type, answer, options } ] } ]
//   }
// Sessions are ordered oldest -> newest server side.
// ---------------------------------------------------------------------------

const toTime = (value) => {
    const t = Date.parse(value || "");
    return Number.isNaN(t) ? 0 : t;
};

const normaliseType = (value) => String(value == null ? "" : value).trim().toLowerCase();

export const isReviewAppointment = (value) => normaliseType(value) !== "initial" && normaliseType(value) !== "";

// Column/section labels for the Previous Data tables. Kept as a plain
// capitaliser rather than pulling in the prefix wording, because a table cell
// wants the short form ("Initial" / "Review") and a group header wants the long
// one ("Initial Appointment - ...").
export const isInitialAppointmentType = (value) => !isReviewAppointment(value);

export const formatAppointmentLabel = (value) => {
    const v = String(value == null ? "" : value).trim();
    if (!v) return "-";
    // Review Smart Goal is its own appointment type, not a plain Review, and it
    // runs as a separate pass in the client's history - reading it as "Review"
    // collapsed two different columns into one in the Previous Data tables.
    if (v.toLowerCase() === "review smart goal") return "Review Smart Goal";
    return isInitialAppointmentType(v) ? "Initial" : "Review";
};

// A session counts as "completed enough to inherit from" when at least one
// question in it actually carries an answer. HINE/HAI answers arrive as
// { value, entries } so both shapes are checked.
const hasAnsweredQuestions = (session) => {
    const pathway = Array.isArray(session?.pathway) ? session.pathway : [];
    return pathway.some((q) => {
        const answer = q?.answer;
        if (answer === null || answer === undefined || answer === "") return false;
        if (Array.isArray(answer)) return answer.length > 0;
        if (typeof answer === "object") {
            if (answer.value) return true;
            return Array.isArray(answer.entries) && answer.entries.length > 0;
        }
        return true;
    });
};

// ---------------------------------------------------------------------------
// Data Continuity: pick the session a new Review inherits its data from.
//
// Spec: "pre-fill data automatically from the latest pathway (Initial or
// previous Review)". The client runs the multi-cycle flow Initial -> RSG
// (optional) -> Review -> RSG (optional) -> Review -> ..., so the source for a
// NEW Review is the PREVIOUS Review session (appointment_type "review") once
// one exists, regardless of whether an RSG was run in between. Review Smart
// Goal is an optional interleaved pass, so a newer RSG run never becomes the
// source for a plain Review; RSG is only the source when no Review exists yet.
// The Initial is the final fallback for a client with no review-style history.
//
// `excludeSessionId` is the session currently being answered. It MUST be
// excluded: get_historical_pathway_data.php returns every session for the
// client, and because it has no "created_at" it is ordered by started_at, so
// the session being edited is almost always the newest row in the list. Without
// this guard the "newest answered session" is the session itself, and the
// pre-fill copies the draft's own partially-answered questions back onto
// itself instead of inheriting anything - which also freezes answers the
// therapist has since changed.
// ---------------------------------------------------------------------------
export const pickInheritanceSession = (history, excludeSessionId = null) => {
    const sessions = Array.isArray(history?.data) ? history.data : [];
    if (sessions.length === 0) return null;

    const excluded = excludeSessionId != null && excludeSessionId !== ""
        ? String(excludeSessionId)
        : null;

    const candidates = excluded === null
        ? sessions
        : sessions.filter(s => String(s?.session_id ?? s?.sessionId ?? "") !== excluded);

    if (candidates.length === 0) return null;

    const answered = candidates.filter(hasAnsweredQuestions);
    if (answered.length > 0) {
        const byNewest = (list) => [...list].sort((a, b) => toTime(a.assessment_date) - toTime(b.assessment_date));
        const newestOfType = (type) => {
            const t = normaliseType(type);
            const matches = answered.filter((s) => normaliseType(s.appointment_type) === t);
            if (matches.length === 0) return null;
            const sorted = byNewest(matches);
            return sorted[sorted.length - 1];
        };

        // Multi-cycle data continuity (Initial -> RSG -> Review -> RSG ->
        // Review -> ...): a new Review inherits from the PREVIOUS Review.
        // Review Smart Goal is an optional pass interleaved between Reviews,
        // so a newer RSG run must not become the source for the next plain
        // Review; RSG is only the source when no plain Review exists yet
        // (newest answered session of any type as the final fallback).
        return newestOfType("review") || newestOfType("review smart goal") || byNewest(answered)[answered.length - 1];
    }

    const initials = candidates.filter((s) => normaliseType(s.appointment_type) === "initial");
    return initials.length > 0 ? initials[initials.length - 1] : null;
};

// ---------------------------------------------------------------------------
// Group-name prefix: the group headers are named after the appointment that
// produced them, so a Review question never reads as if it were an Initial one.
// Wording lives here so it can be retuned in one place.
// ---------------------------------------------------------------------------
const APPOINTMENT_GROUP_PREFIX = {
    initial: "Initial Appointment",
    review: "Review Appointment",
};

export const groupPrefixForAppointment = (appointment) =>
    APPOINTMENT_GROUP_PREFIX[isReviewAppointment(appointment) ? "review" : "initial"];

export const withAppointmentPrefix = (groupName, appointment) => {
    const name = String(groupName || "").trim();
    if (!name) return name;
    return `${groupPrefixForAppointment(appointment)} - ${name}`;
};

// ---------------------------------------------------------------------------
// Group list for the "Previous Data" file bar. Demographics is hoisted to the
// front because it is the entry point most therapists open first; the rest
// keep their pathway order.
// ---------------------------------------------------------------------------
export const orderHistoryGroups = (history, pathwayGroups) => {
    const fromApi = Array.isArray(history?.groups) ? history.groups : [];
    const fromPathway = Array.isArray(pathwayGroups) ? pathwayGroups : [];

    // UNION of both sources, history first.
    //
    // The history list spans every pathway the client has been assessed on, but
    // the bar is opened from inside a Review session, and a Review pathway
    // carries groups the Initial one does not (e.g. "Goal Progression"). Taking
    // only the history list hid those, and taking only the active pathway hid
    // the groups the client reached in earlier sessions. Merging keeps both.
    // Deduplicated case-insensitively, first occurrence wins.
    const seen = new Set();
    const groups = [...fromApi, ...fromPathway]
        .map((g) => (typeof g === "string" ? { group_name: g } : g))
        .filter((g) => {
            if (!g || !g.group_name) return false;
            const key = normaliseType(g.group_name);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });

    const isDemographics = (g) => normaliseType(g.group_name) === "demographics";
    const demographics = groups.filter(isDemographics);
    const others = groups.filter((g) => !isDemographics(g));
    if (demographics.length > 0) return [...demographics, ...others];

    // "Demographics" is a virtual group and is deliberately always offered.
    //
    // It is not a row in the `pathways` table, so get_historical_pathway_data.php
    // can never list it in `groups` - and the file bar is built from that list,
    // which is why the group's own data (appointment type / client / client id /
    // age group for each past session) had no way to be reached. The API DOES
    // answer a group_name = "Demographics" request with one row per past
    // session, and start-assessment renders those rows in a dedicated table, so
    // the entry is prepended here and hoisted to the front as the usual entry
    // point.
    return [{ group_name: "Demographics" }, ...others];
};

// ---------------------------------------------------------------------------
// Answer formatter shared by the inline Previous Data tables.
// ---------------------------------------------------------------------------
export const formatHistoryAnswer = (answer) => {
    if (answer === null || answer === undefined || answer === "") return "-";
    if (typeof answer === "object") {
        if (Array.isArray(answer)) {
            return answer.map((a) => formatHistoryAnswer(a)).filter((a) => a !== "-").join(", ") || "-";
        }
        if (Array.isArray(answer.entries)) {
            return answer.entries.map((e) => JSON.stringify(e)).join(" | ");
        }
        if (answer.value) return String(answer.value);
        return JSON.stringify(answer);
    }
    return String(answer);
};
